import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';

import {
	getPowerUser,
	deletePowerUser,
	updatePowerUser,
} from '../../api/power-users';
import { getAssets } from '../../api/assets';
import { addToWatchlist, removeFromWatchlist } from '../../api/watchlist';
import { getUserFullName } from '../../components/helpers/functions';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { AvatarGenerated } from '../../components/Avatar';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../../components/EllipsisDropdown';
import { StackableModal, AssetSelectorBeta } from '../../components/nextgen';
import Button from '../../components/Button';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSettings from '../../hooks/useSettings';
import { parseAssetTemplate } from '../../components/nextgen/AssetSelectorBeta';

const PowerUserProfile = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	const {
		value: powerUser,
		execute: refreshPowerUser,
		status,
	} = useAsync(getPowerUser, { immediate: false });

	const { execute: onDeletePowerUser } = useAsync(deletePowerUser, {
		immediate: false,
		showNotification: { success: 'Power user deleted' },
	});

	const { execute: onUpdatePowerUser, status: updatingPowerUser } = useAsync(
		updatePowerUser,
		{
			immediate: false,
			showNotification: { success: 'Power user updated', error: 'raw' },
		},
	);

	const { setOpen: setDeleteModalOpen, setClose: setDeleteModalClose } =
		useModal('deleteModal');

	const [addAssetModalOpen, setAddAssetModalOpen] = useState(false);
	const [selectedAssetId, setSelectedAssetId] = useState(null);

	// Edit modal state
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editForm, setEditForm] = useState({
		email: '',
		firstName: '',
		lastName: '',
	});

	const { value: assetsData } = useAsync(getAssets, {
		defaultPayload: { pageSize: null },
	});

	const { execute: onAddToWatchlist, status: addingAsset } = useAsync(
		addToWatchlist,
		{
			immediate: false,
			showNotification: { success: 'Asset assigned successfully' },
		},
	);

	const { execute: onRemoveFromWatchlist } = useAsync(removeFromWatchlist, {
		immediate: false,
		showNotification: { success: 'Asset removed successfully' },
	});

	const settings = useSettings();
	const assetDisplayTemplate = settings.get('assetDisplay');

	useEffect(() => {
		if (id) {
			refreshPowerUser({ id });
		}
	}, [id, refreshPowerUser]);

	// Filter out already assigned assets and format for AssetSelectorBeta
	const availableAssets = useMemo(() => {
		if (!assetsData?.rows) return [];
		const assignedAssetIds = powerUser?.assets?.map((a) => a.id) || [];
		return assetsData.rows
			.filter((asset) => !assignedAssetIds.includes(asset.id))
			.map((asset) => ({
				id: asset.id,
				name:
					[
						asset.manufacturer?.name || asset.manufacturerName,
						asset.model?.name || asset.modelName,
					]
						.filter(Boolean)
						.join(' ') || 'Unnamed Asset',
				manufacturer:
					asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
				location: asset.location?.name || 'No location',
				serial: asset.serialNumber || 'N/A',
				serialNumber: asset.serialNumber,
			}));
	}, [assetsData, powerUser?.assets]);

	const handleAddAsset = () => {
		if (!selectedAssetId) return;
		onAddToWatchlist(
			{
				asset_id: selectedAssetId,
				power_user_id: powerUser.id,
			},
			() => {
				setAddAssetModalOpen(false);
				setSelectedAssetId(null);
				refreshPowerUser({ id });
			},
		);
	};

	const handleOpenEditModal = () => {
		setEditForm({
			email: powerUser?.email || '',
			firstName: powerUser?.firstName || '',
			lastName: powerUser?.lastName || '',
		});
		setEditModalOpen(true);
	};

	const handleSaveEdit = () => {
		onUpdatePowerUser(
			{
				id: powerUser.id,
				...editForm,
			},
			() => {
				setEditModalOpen(false);
				refreshPowerUser({ id });
			},
		);
	};

	const getDisplayName = () => {
		if (!powerUser) return '';
		return (
			getUserFullName(powerUser) || powerUser.email || 'Unnamed Power User'
		);
	};

	return (
		<MainContent maxWidth="w-full bg-gray-50 min-h-screen">
			{status === 'pending' && !powerUser ? (
				<div className="max-w-5xl mx-auto">
					<LoadingSkeleton />
				</div>
			) : !powerUser ? (
				<div className="text-center py-12">
					<p className="text-sm text-gray-500">Power user not found</p>
				</div>
			) : (
				<div className="max-w-5xl mx-auto">
					{/* Header */}
					<PageHeading
						RightElement={
							<div className="flex items-center gap-x-2">
								<Button type="secondary" onClick={handleOpenEditModal}>
									Edit
								</Button>
								<EllipsisDropdown>
									<EllipsisDropdownItem
										danger
										onClick={() =>
											setDeleteModalOpen({
												title:
													'Are you sure you want to delete this power user?',
												description:
													'This action cannot be undone. The power user will no longer have access to assigned assets.',
												confirmText: 'Delete Power User',
												onConfirm: () =>
													onDeletePowerUser(powerUser?.id, () => {
														setDeleteModalClose();
														navigate('/manage/team');
													}),
											})
										}
									>
										Delete Power User
									</EllipsisDropdownItem>
								</EllipsisDropdown>
							</div>
						}
						divider={false}
					>
						<button
							onClick={() => navigate('/manage/team')}
							className="text-sm text-gray-500 hover:text-gray-700 font-normal"
						>
							← Back to Team
						</button>
					</PageHeading>

					<div className="">
						<div className="grid grid-cols-5 gap-x-6">
							{/* Left Sidebar - Contact Info */}
							<div className="col-span-2">
								<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="flex items-center gap-3 py-4 px-6 border-b border-gray-200">
										<AvatarGenerated size="sm" id={getDisplayName()} />
										<h3 className="text-sm font-medium text-gray-900">
											{getDisplayName()}
										</h3>
									</div>

									<dl className="grid grid-cols-1 gap-y-4 px-6 pb-6">
										<div className="flex items-start gap-3">
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Email
												</dt>
												<dd className="mt-1 text-sm text-gray-900 break-words">
													{powerUser.email ? (
														<a
															href={`mailto:${powerUser.email}`}
															className="text-indigo-600 hover:text-indigo-700"
														>
															{powerUser.email}
														</a>
													) : (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													First Name
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{powerUser.firstName || (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>

										<div className="flex items-start gap-3">
											<div className="min-w-0 flex-1">
												<dt className="text-xs font-medium text-gray-500">
													Last Name
												</dt>
												<dd className="mt-1 text-sm text-gray-900">
													{powerUser.lastName || (
														<span className="text-gray-400">—</span>
													)}
												</dd>
											</div>
										</div>
									</dl>
								</div>

								{/* Notifications Toggle */}
								{/*<div className="bg-white border-gray-200 border rounded-lg mt-6">
									<div className="py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Notifications
										</h3>
									</div>
									<div className="px-6 py-4">
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-3">
												{powerUser.notifications ? (
													<BellIcon className="w-5 h-5 text-indigo-600" />
												) : (
													<BellSlashIcon className="w-5 h-5 text-gray-400" />
												)}
												<div>
													<p className="text-sm font-medium text-gray-900">
														Email Notifications
													</p>
													<p className="text-xs text-gray-500">
														{powerUser.notifications
															? 'Receiving notifications for assigned assets'
															: 'Notifications are disabled'}
													</p>
												</div>
											</div>
											<button
												type="button"
												onClick={handleToggleNotifications}
												disabled={togglingNotifications === 'pending'}
												className={`
													relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
													transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
													${powerUser.notifications ? 'bg-indigo-600' : 'bg-gray-200'}
													${togglingNotifications === 'pending' ? 'opacity-50 cursor-not-allowed' : ''}
												`}
											>
												<span
													className={`
														pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
														transition duration-200 ease-in-out
														${powerUser.notifications ? 'translate-x-5' : 'translate-x-0'}
													`}
												/>
											</button>
										</div>
									</div>
								</div>*/}
							</div>

							{/* Right Sidebar - Assets */}
							<div className="col-span-3">
								<div className="bg-white rounded-lg border border-gray-200 mt-6">
									<div className="flex items-center justify-between py-4 px-6 border-b border-gray-200">
										<h3 className="text-sm font-medium text-gray-900 flex items-center">
											Assigned Assets{' '}
											{powerUser.assets &&
												powerUser.assets.length > 0 &&
												`(${powerUser.assets.length})`}
										</h3>
										<Button
											type="secondary"
											size="sm"
											Icon={PlusIcon}
											onClick={() => setAddAssetModalOpen(true)}
										>
											Add Asset
										</Button>
									</div>
									{powerUser.assets && powerUser.assets.length > 0 ? (
										<ul className="divide-y divide-gray-200 m-0 p-0 !mt-0">
											{powerUser.assets.map((asset) => (
												<li
													key={asset.id}
													className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
													onClick={() =>
														navigate(`/assets/${asset.id}/overview`)
													}
												>
													<div className="flex items-center justify-between">
														<div className="min-w-0 flex-1">
															<p className="text-sm font-medium text-gray-900 truncate">
																{[
																	asset.manufacturer?.name ||
																		asset.manufacturerName,
																	asset.model?.name || asset.modelName,
																]
																	.filter(Boolean)
																	.join(' ') || 'Unnamed Asset'}
															</p>
															<p className="text-xs text-gray-500 truncate">
																{assetDisplayTemplate
																	? parseAssetTemplate(
																			assetDisplayTemplate,
																			asset,
																		)
																	: `${asset.location?.name || 'No location'} · SN: ${asset.serialNumber || 'N/A'}`}
															</p>
														</div>
														<div onClick={(e) => e.stopPropagation()}>
															<EllipsisDropdown>
																<EllipsisDropdownItem
																	danger
																	onClick={() =>
																		setDeleteModalOpen({
																			title: 'Remove asset from power user?',
																			description:
																				'This power user will no longer have access to this asset.',
																			confirmText: 'Remove Asset',
																			onConfirm: () =>
																				onRemoveFromWatchlist(
																					asset.watchlistId,
																					() => {
																						setDeleteModalClose();
																						refreshPowerUser({ id });
																					},
																				),
																		})
																	}
																>
																	Remove asset
																</EllipsisDropdownItem>
															</EllipsisDropdown>
														</div>
													</div>
												</li>
											))}
										</ul>
									) : (
										<div className="px-6 py-8 text-center">
											<p className="text-sm text-gray-500">
												No assets assigned
											</p>
											<p className="text-xs text-gray-400 mt-1">
												Assign this power user to assets to give them access
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Add Asset Modal */}
			<StackableModal
				open={addAssetModalOpen}
				onClose={() => {
					setAddAssetModalOpen(false);
					setSelectedAssetId(null);
				}}
				title="Add Asset"
				size="md"
			>
				<div className="space-y-4">
					<p className="text-sm text-gray-500 mb-3">
						Select an asset to assign to this power user.
					</p>
					<AssetSelectorBeta
						value={selectedAssetId}
						onChange={setSelectedAssetId}
						assets={availableAssets}
						placeholder="Search and select an asset..."
						multiple={false}
						disablePortal
						className="mt-3"
					/>
					<div className="flex justify-end gap-3 pt-4">
						<Button
							type="secondary"
							onClick={() => {
								setAddAssetModalOpen(false);
								setSelectedAssetId(null);
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							onClick={handleAddAsset}
							disabled={!selectedAssetId || addingAsset === 'pending'}
						>
							{addingAsset === 'pending' ? 'Adding...' : 'Add Asset'}
						</Button>
					</div>
				</div>
			</StackableModal>

			{/* Edit Power User Modal */}
			<StackableModal
				open={editModalOpen}
				onClose={() => setEditModalOpen(false)}
				title="Edit Power User"
				size="md"
			>
				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Email <span className="text-red-500">*</span>
						</label>
						<input
							type="email"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="email@example.com"
							onChange={(e) =>
								setEditForm({ ...editForm, email: e.target.value })
							}
							value={editForm.email}
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							First Name{' '}
							<span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="John"
							onChange={(e) =>
								setEditForm({ ...editForm, firstName: e.target.value })
							}
							value={editForm.firstName}
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Last Name{' '}
							<span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							placeholder="Doe"
							onChange={(e) =>
								setEditForm({ ...editForm, lastName: e.target.value })
							}
							value={editForm.lastName}
						/>
					</div>

					<div className="flex justify-end gap-3 pt-4">
						<Button type="secondary" onClick={() => setEditModalOpen(false)}>
							Cancel
						</Button>
						<Button
							disabled={!editForm.email || updatingPowerUser === 'pending'}
							onClick={handleSaveEdit}
						>
							{updatingPowerUser === 'pending' ? 'Saving...' : 'Save Changes'}
						</Button>
					</div>
				</div>
			</StackableModal>
		</MainContent>
	);
};

export default PowerUserProfile;
